/**
 * Sticky behaviour controller
 *
 * 1. Check sticky element sibling comes into view
 * 2. Check sibling scrolls over sticky element
 * 3. Hide / show underlying sticky elements that do not hide underlying elements by itself
 */
import { classNames } from '../_constants';

const observerClass = '.' + classNames.sticky.hideUnderlying;
let stickyInitialized: boolean = false;

/**
 * Get all previous sticky elements that do not hide underlying elements by itself
 */
function getStickySiblings(element: Element): HTMLElement[] {
  let siblings: HTMLElement[] = [];
  if(!element.parentNode) {
    return siblings;
  }

  let isPrev: boolean = true;
  Array.from(element.parentNode.children).forEach(node => {
    if (node == element) {
      isPrev = false;
    }
    // find previous sticky elements
    if (isPrev && node instanceof HTMLElement && node.classList.contains(classNames.sticky.stencilSticky)) {
      // clear siblings when sticky element hides underlying by itself
      if (node.classList.contains(classNames.sticky.hideUnderlying)) {
        siblings = [];
      }
      // push sibling to list
      siblings.push(node);
    }
  });

  return siblings;
}

/**
 * Check for div, section cms-*
 */
function isValidTag(element: Element): boolean {
  if (element == null || element.tagName == null) {
    return false;
  }
  const tag = element.tagName.toLowerCase();
  return tag == 'div' || tag == 'section' || tag.startsWith('cms-');
}

/**
 * Hide/show prev siblings of this sticky element by scroll event
 *
 * @param stickySection: the sticky DOM | shadowRoot section
 * @param stickySibling: div or section next to sticky element
 * @param prevSiblings: list of previous siblings to control
 */
function scrollingEvent (stickySection, stickySibling, prevSiblings) {
  const stickySectionRect = stickySection.getBoundingClientRect();
  const stickySiblingRect = stickySibling.getBoundingClientRect();
  prevSiblings.forEach(node => {
    node.classList.toggle(classNames.sticky.hidden, (stickySiblingRect.top < stickySectionRect.bottom));
  });
}

/**
 * Observes sticky sibling comes into view
 *
 * @param stickySibling: div or section next to sticky element
 */
function observerCallback([stickySibling]) {
  const stickyElement = this;
  // check for shadowRoots
  // sticky element contains class sticky
  let stickySection = stickyElement;
  if (stickyElement.shadowRoot) {
    stickySection = stickyElement.shadowRoot.querySelector('.' + classNames.sticky.section);
  }
  // sibling contains div or section
  let sibling = stickySibling.target;
  if (sibling.shadowRoot) {
    sibling = Array.from(sibling.shadowRoot.children).find((elm: HTMLElement) => isValidTag(elm));
  }
  if (!stickySection || !sibling) {
    return;
  }

  // setup control scrolling
  const prevSiblings = getStickySiblings(stickyElement);
  const scrollEvent = scrollingEvent.bind(stickyElement, stickySection, sibling, prevSiblings);
  if (stickySibling.isIntersecting) {
    document.addEventListener('scroll', scrollEvent);
  } else {
    document.removeEventListener('scroll', scrollEvent);
  }
}

/**
 * Setup element observer for sticky element sibling
 */
function stickyObserver() {
  stickyInitialized = true;
  const stickyElements = document.querySelectorAll(observerClass);
  if (!stickyElements || stickyElements.length == 0) {
    return;
  }

  // create observer for each sticky element
  stickyElements.forEach(stickyElement => {
    let stickySibling = stickyElement.nextElementSibling;
    while (stickySibling != null && !isValidTag(stickySibling)) {
      stickySibling = stickySibling.nextElementSibling;
    }
    if (!stickySibling) {
      return;
    }

    const stickyObserver = new IntersectionObserver(observerCallback.bind(stickyElement));
    stickyObserver.observe(stickySibling);
  });
}

/**
 * Initializer: readyState or DOMContentLoaded
 */
export function initStickyObserver() {
  if (stickyInitialized) {
    return;
  }

  //document.addEventListener('DOMContentLoaded', function () {
  window.addEventListener('load', function () {
    stickyObserver();
  });
}
